import type { WorkspacePlan, WorkspaceUsageRow } from '@floorpoll/shared';

function relative(iso: string | null): string {
  if (!iso) return 'never';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Plan names for the operator, who *does* need to see how a workspace came by
 * its plan — unlike the workspace's own members, who are shown a plan and not a
 * note about who paid for it.
 */
const PLAN_LABELS: Record<WorkspacePlan, string> = {
  free: 'Free',
  marquee: 'Marquee',
  granted: 'Granted',
};

/**
 * A comp sitting on top of a live subscription. Revoking it is safe — the
 * subscription is a different component and survives — but an operator should
 * be able to see that before deciding, not have to trust it.
 */
const planLabel = (row: WorkspaceUsageRow) =>
  row.plan === 'granted' && row.subscribed ? 'Granted · also subscribed' : PLAN_LABELS[row.plan];

/**
 * The cross-workspace table on the operator usage page.
 *
 * Its own component, rather than inline JSX in the page, so the dev gallery
 * can render the same empty state the real page would show instead of a
 * hand-copied clone that could quietly drift from it.
 */
export function WorkspaceUsageTable({
  workspaces,
  actions,
  busyWorkspaceId = null,
}: {
  workspaces: WorkspaceUsageRow[];
  /**
   * Grant and revoke together, or neither at all.
   *
   * One prop rather than two optional handlers, so "half wired" cannot be
   * expressed: with two, a caller passing only `onGrant` would still render
   * Revoke buttons on granted rows and they would silently do nothing — the
   * dead control this component's read-only mode exists to avoid. The dev
   * gallery omits this entirely and gets no action column.
   */
  actions?: {
    grant: (workspaceId: string) => void;
    revoke: (workspaceId: string) => void;
  };
  busyWorkspaceId?: string | null;
}) {
  const actionable = actions !== undefined;

  return (
    <div className="panel">
      <h2 className="panel-title">Workspaces</h2>
      <p className="panel-caption">Sorted by most recent activity</p>
      {workspaces.length === 0 ? (
        <p className="muted">No workspaces yet.</p>
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Workspace</th>
                <th>Plan</th>
                <th>Members</th>
                <th>Polls</th>
                <th>Sessions run</th>
                <th>Votes in sessions</th>
                <th>Created</th>
                <th>Last active</th>
                {actionable ? <th>Plan actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {workspaces.map((row) => (
                <tr key={row.workspaceId}>
                  <td>
                    {row.isLiveNow ? <span className="lamp" /> : null} {row.name}
                  </td>
                  <td>{planLabel(row)}</td>
                  <td>{row.members}</td>
                  <td>{row.quizzes}</td>
                  <td>{row.sessionsStarted}</td>
                  <td>{row.votesInSessions}</td>
                  <td>{new Date(row.createdAt).toLocaleDateString()}</td>
                  <td>{row.isLiveNow ? 'live now' : relative(row.lastActiveAt)}</td>
                  {actionable ? (
                    <td>
                      {/* The workspace is named in the accessible label because
                          a table of identical "Grant" buttons tells a screen
                          reader nothing about which row it is on. */}
                      {row.plan === 'granted' ? (
                        <button
                          type="button"
                          className="secondary"
                          disabled={busyWorkspaceId !== null}
                          aria-label={`Revoke ${row.name}’s grant`}
                          onClick={() => actions.revoke(row.workspaceId)}
                        >
                          Revoke
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="secondary"
                          disabled={busyWorkspaceId !== null}
                          aria-label={`Grant ${row.name} Marquee`}
                          onClick={() => actions.grant(row.workspaceId)}
                        >
                          Grant
                        </button>
                      )}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="muted">
        No poll titles, question text, or results — the workspace is as deep as this view goes.
      </p>
    </div>
  );
}
